import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { StudioClientProvider, createClient } from '@studio/api-client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as JotaiProvider } from 'jotai';
import { type ReactNode, useMemo } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

interface InnerProvidersProps {
  apiBaseUrl: string;
  children: ReactNode;
}

/**
 * Builds the Studio RPC client with a fetch override that attaches the
 * Clerk session token (when signed in) and `credentials: 'include'` so the
 * `__session` cookie reaches a same-site backend in dev.
 */
function StudioRpcProvider({ apiBaseUrl, children }: InnerProvidersProps) {
  const { getToken } = useAuth();

  const client = useMemo(
    () =>
      createClient({
        baseUrl: apiBaseUrl,
        fetch: async (input, init) => {
          const headers = new Headers(init?.headers);
          const token = await getToken();
          if (token && !headers.has('authorization')) {
            headers.set('authorization', `Bearer ${token}`);
          }
          return fetch(input, { ...init, headers, credentials: 'include' });
        },
      }),
    [apiBaseUrl, getToken],
  );

  return <StudioClientProvider client={client}>{children}</StudioClientProvider>;
}

export interface StudioProvidersProps {
  /** Origin of the backend (e.g. `import.meta.env.VITE_API_URL`). */
  apiBaseUrl: string;
  /** Clerk publishable key (e.g. `import.meta.env.VITE_CLERK_PUBLISHABLE_KEY`). */
  clerkPublishableKey: string;
  children: ReactNode;
}

/**
 * Top-level provider stack:
 *   ClerkProvider → QueryClientProvider → StudioRpcProvider → JotaiProvider
 * Mounted once in `main.tsx` around `<App />`.
 */
export function StudioProviders({
  apiBaseUrl,
  clerkPublishableKey,
  children,
}: StudioProvidersProps) {
  return (
    <ClerkProvider publishableKey={clerkPublishableKey}>
      <QueryClientProvider client={queryClient}>
        <StudioRpcProvider apiBaseUrl={apiBaseUrl}>
          <JotaiProvider>{children}</JotaiProvider>
        </StudioRpcProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}
